
import { useState, useEffect, useCallback } from 'react';

/**
 * A generic hook to fetch data using the API layer.
 * It handles loading states, errors, and provides a refresh mechanism.
 * 
 * @param fetcher The async service function to call (e.g. BillingService.getSubscriptions)
 * @param dependencies Array of dependencies that should trigger a re-fetch
 */
export function useDataTemplate<T>(
  fetcher: () => Promise<T>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [fetcher]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [...dependencies, refresh]);

  return { data, isLoading, error, refresh, setData };
}
